/**
 * Module 6D Phase 3 — Read-only impersonation (frontend).
 *
 * Token isolation model (the user's hard requirement):
 *   1. Impersonation is launched in a NEW TAB. The admin's `/admin`
 *      session stays untouched in its original tab.
 *   2. The imp_token is delivered via URL hash on the launch URL, e.g.
 *        /impersonate/launch#imp_token=imp_xxx&studio_id=stu_xxx
 *                            &studio_label=Acme&expires_at=2026-...
 *      The hash is consumed and REMOVED from the URL synchronously on
 *      first paint (see `consumeImpersonationHash`) so the token can't
 *      linger in browser history / copy-paste.
 *   3. The consumed token lives in **sessionStorage** (tab-scoped).
 *      Closing the tab effectively ends the impersonation client-side.
 *      Server-side: the impersonation row TTL (30 min) and parent-death
 *      check still own the actual security boundary.
 *   4. The token NEVER touches cookies and never co-mingles with the
 *      regular studio session (different storage key, different scope).
 */

export const IMP_TOKEN_KEY    = "ls.imp.token";
export const IMP_STUDIO_KEY   = "ls.imp.studio_id";
export const IMP_LABEL_KEY    = "ls.imp.studio_label";
export const IMP_EXPIRES_KEY  = "ls.imp.expires_at";
export const IMP_ADMIN_KEY    = "ls.imp.admin_email";

function _ss() {
  try { return window.sessionStorage; } catch { return null; }
}

/** Read the active impersonation token, if any. */
export function getImpToken() {
  const s = _ss();
  if (!s) return null;
  try { return s.getItem(IMP_TOKEN_KEY); } catch { return null; }
}

/** Read the full impersonation context for the banner. */
export function getImpContext() {
  const s = _ss();
  if (!s) return null;
  try {
    const token = s.getItem(IMP_TOKEN_KEY);
    if (!token) return null;
    return {
      token,
      studio_id:    s.getItem(IMP_STUDIO_KEY),
      studio_label: s.getItem(IMP_LABEL_KEY),
      expires_at:   s.getItem(IMP_EXPIRES_KEY),
      admin_email:  s.getItem(IMP_ADMIN_KEY),
    };
  } catch { return null; }
}

/**
 * Synchronous one-shot consumption of `#imp_token=…` on the launch URL.
 * Returns true if a token was consumed (caller may want to redirect to
 * /dashboard). Strips the hash on success so refresh + history don't
 * carry the token.
 */
export function consumeImpersonationHash() {
  if (typeof window === "undefined") return false;
  const hash = window.location.hash || "";
  if (!hash.includes("imp_token=")) return false;
  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const token = params.get("imp_token");
  if (!token || !token.startsWith("imp_")) return false;
  const s = _ss();
  if (!s) return false;
  try {
    s.setItem(IMP_TOKEN_KEY,    token);
    s.setItem(IMP_STUDIO_KEY,   params.get("studio_id")    || "");
    s.setItem(IMP_LABEL_KEY,    params.get("studio_label") || "");
    s.setItem(IMP_EXPIRES_KEY,  params.get("expires_at")   || "");
    s.setItem(IMP_ADMIN_KEY,    params.get("admin_email")  || "");
  } catch { /* private mode */ }
  // Strip the hash immediately so the token isn't visible in the URL
  // bar or copy-pasted from history. Use replaceState — no history entry.
  try {
    window.history.replaceState(null, "",
      window.location.pathname + window.location.search);
  } catch { /* ignore */ }
  return true;
}

/** Clear the impersonation context from sessionStorage. */
export function clearImpersonation() {
  const s = _ss();
  if (!s) return;
  try {
    s.removeItem(IMP_TOKEN_KEY);
    s.removeItem(IMP_STUDIO_KEY);
    s.removeItem(IMP_LABEL_KEY);
    s.removeItem(IMP_EXPIRES_KEY);
    s.removeItem(IMP_ADMIN_KEY);
  } catch { /* ignore */ }
}
